import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Typography } from '@mui/material';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { handleDeleteDialogError } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

import { useAppDispatch } from '../../app/hooks';
import { DeleteDialog } from '../../common/components/deleteDialog/DeleteDialog';
import { setDialogOpen } from '../../common/components/deleteDialog/deleteDialogSlice';
import { setRows } from '../../common/components/PalmDataGrid/palmDataGridSlice';
import { deleteAclResource } from '../../common/services/api/Acls/AclResources.thunk';
import { DeleteDialogContentProps } from '../../common/types/TypeDefs';

export function AclResourcesDeleteDialogContent({
    title,
    itemId,
    editingRow,
}: Readonly<DeleteDialogContentProps<{ name: string, displayName: string }>>) {
    const { name, displayName } = editingRow ?? {};
    const { enqueueSnackbar } = useSnackbar();
    const [processing, setProcessing] = useState<boolean>(false);

    const successHandler = (): void => {
        dispatch(setDialogOpen(false));
        enqueueSnackbar('ACL Resource has been deleted.', { variant: 'success' });
    };

    const dispatch = useAppDispatch();
    const params = useParams();
    const tenantId = params.tenantId;

    const aclResourceDeleteCallback = useCallback(() => {
        if (itemId) {
            setProcessing(true);
            dispatch(deleteAclResource({
                tenantId: tenantId ?? '',
                id: itemId.toString()
            }))
                .unwrap()
                .then((result) => {
                    dispatch(setRows(result.rows.map(row => ({ ...row, conditionsDisplay: row.conditions?.join(', ') ?? '' }))));
                    successHandler();
                    setProcessing(false);
                })
                .catch((error) => {
                    handleDeleteDialogError('ACL Resource', enqueueSnackbar, extractStatusCode(error.message));
                    setProcessing(false);
                });
        }
    }, [itemId]);

    return  (
        <DeleteDialog deleteCallback={aclResourceDeleteCallback} processing={processing ?? false} title={title}>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                ACL Resource Name: <Typography component="span" color="text.primary" fontWeight="400">{name}</Typography>
            </Typography>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                ACL Resource Display Name: <Typography component="span" color="text.primary" fontWeight="400">{displayName}</Typography>
            </Typography>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                Tenant Id: <Typography component="span" color="text.primary" fontWeight="400">{tenantId}</Typography>
            </Typography>
        </DeleteDialog>
    );
}
